/**
 * Shared rendering helpers for hub operational alerts (Status & Notices).
 *
 * Single source of truth for what an alert `category` means, used by both
 * `wayai alerts` (interactive view) and `wayai report create` (auto-attaches a
 * compact alert summary so an invalid-credential signal reaches triage even when
 * the reporter didn't think to look).
 */

import { AUTOMATION_ALERT_CATEGORIES, AUTOMATION_BLOCKED_ALERT_CATEGORY, type HubAlert } from '@wayai/core/contracts';

/** Severity → a single-glyph prefix for the alert line. */
export function severityGlyph(severity: HubAlert['severity']): string {
  switch (severity) {
    case 'critical': return '✖';
    case 'warn': return '⚠';
    default: return 'ℹ';
  }
}

/** `provider status_code` (either may be absent) — the shared right-hand detail on an alert line. */
export function formatProviderBits(alert: HubAlert): string {
  return [alert.provider, alert.status_code != null ? `${alert.status_code}` : null]
    .filter(Boolean)
    .join(' ');
}

/**
 * What the alert is about: the connection (optionally resolved to a friendly
 * label via `resolveLabel`), else the channel, else a dash. Shared so the
 * interactive view and the report summary render the target identically.
 */
export function formatAlertTarget(
  alert: HubAlert,
  resolveLabel?: (connectionId: string) => string | undefined,
): string {
  if (alert.connection_id) {
    return resolveLabel?.(alert.connection_id) ?? `connection ${alert.connection_id}`;
  }
  if (alert.channel_id) return `channel ${alert.channel_id}`;
  return '—';
}

/**
 * Category → (label, remediation hint). Categories are an open string (new
 * producers add their own), so an unknown category degrades to a generic hint
 * rather than being dropped.
 */
export function describeCategory(category: string): { label: string; hint: string } {
  switch (category) {
    case 'connection_auth':
      return {
        label: 'Credential rejected',
        hint: 'The provider rejected this connection\'s credential (auth). Update the API key/credential on the connection.',
      };
    case 'connection_quota':
      return {
        label: 'Quota / rate limit',
        hint: 'The provider returned a quota or rate-limit error. Check the provider account\'s usage/billing.',
      };
    // gh #3164 — a 400 is a request/config rejection, NOT a bad credential. Labeling
    // it "Credential rejected" points admins at rotating a healthy key.
    case 'connection_config':
      return {
        label: 'Request config rejected',
        hint: 'The provider rejected the request configuration (400). Check the connection\'s model or settings (e.g. an unsupported response_format) — the credential is fine.',
      };
    case 'channel_token':
      return {
        label: 'Channel token invalid',
        hint: 'The channel token is expired or revoked. Reconnect the channel to refresh it.',
      };
    case 'tool_auth':
      return {
        label: 'Tool auth failed',
        hint: 'An external tool/MCP server rejected auth. Re-authorize the tool connection.',
      };
    case 'production_credential_missing':
      return {
        label: 'Production credential missing',
        hint: 'A production connection has no credential. Set it with `wayai set-connection-credential`.',
      };
    // wayai#4931 PR 2b, PR 2c: an automation whose fire reached no list — a hub list or an org list.
    case AUTOMATION_ALERT_CATEGORIES.list_not_found:
      return {
        label: 'Automation list not found',
        hint: 'An automation names a list that no longer exists: a hub list (its `target.list`, kept in `hub.yaml` `contact_lists:`) or an org list (its `target.org_list`, in the organization\'s Contacts settings). Point it at another list, or create one with that name.',
      };
    case AUTOMATION_ALERT_CATEGORIES.list_over_cap:
      return {
        label: 'Automation list over the cap',
        hint: 'An automation\'s list holds more contacts this hub sees than one fire may reach. Narrow the list\'s filter — a hub list in `hub.yaml` `contact_lists:`, an org list in the organization\'s Contacts settings.',
      };
    // wayai#4931 D15: an automation, enabled and not paused, that cannot fire. Its subject is the
    // automation, never a connection; the reason, naming the automation, is the alert's detail.
    case AUTOMATION_BLOCKED_ALERT_CATEGORY:
      return {
        label: 'Automation cannot run',
        hint: 'An automation is enabled but cannot run (its agent, connection or template is gone, or its agent is disabled). Open it in the hub\'s Automations tab, where the reason is shown; the alert clears once it runs again, or is paused, disabled or fixed.',
      };
    default:
      if (category.startsWith('provider:')) {
        return {
          label: 'Provider degraded',
          hint: 'An upstream provider is degraded (platform-level). Usually clears on its own once the provider recovers.',
        };
      }
      return { label: category, hint: 'Tenant-actionable connection/credential alert — inspect the connection in the hub UI.' };
  }
}

/** Active alerts only — the read surface returns active ones, but filter defensively. */
export function activeAlerts(alerts: HubAlert[]): HubAlert[] {
  return alerts.filter((a) => a.status === 'active');
}

/**
 * A compact, credential-safe summary of active alerts for embedding in a bug
 * report's context. Returns null when there are none (nothing to attach). The
 * alert read surface never carries a provider error body, so this is safe to
 * fold into report text verbatim.
 */
export function summarizeActiveAlertsForReport(alerts: HubAlert[]): string | null {
  const active = activeAlerts(alerts);
  if (active.length === 0) return null;

  const lines = active.map((a) => {
    const { label } = describeCategory(a.category);
    const target = formatAlertTarget(a);
    const providerBits = formatProviderBits(a);
    // Credential-safe structured reason (type/code/param) — never the body. Folding
    // it in makes a config rejection self-diagnosable straight from the report. gh #3164.
    const detailBit = a.detail ? ` · ${a.detail}` : '';
    return `- [${a.severity}] ${a.category} (${label}) · ${target}${providerBits ? ` · ${providerBits}` : ''}${detailBit} · seen ${a.count}×`;
  });

  return `Active hub alerts at report time (${active.length}):\n${lines.join('\n')}`;
}
